import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";

const languages = [
  { code: "ar", name: "العربية", flag: "🇸🇦" },
  { code: "he", name: "עברית", flag: "🇮🇱" },
  { code: "en", name: "English", flag: "🇬🇧" },
];

export default function BossSettingLangModal({ visible, onClose }) {
  const { t } = useTranslation();
  const [selectedLang, setSelectedLang] = useState("en");

  // Init from localStorage on open
  useEffect(() => {
    if (visible) {
      try {
        const raw = localStorage.getItem("lang");
        setSelectedLang(raw ? JSON.parse(raw) : "en");
      } catch {
        setSelectedLang("en");
      }
    }
  }, [visible]);

  const saveLanguage = (code) => {
    localStorage.setItem("lang", JSON.stringify(code));
    // Trigger a page reload so all i18n-dependent text updates
    window.location.reload();
  };

  const handleConfirm = () => {
    if (!selectedLang) return;
    saveLanguage(selectedLang);
    onClose();
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black bg-opacity-50"
        onClick={onClose}
      />

      {/* Sheet */}
      <div
        className="relative bg-white rounded-t-3xl w-full max-w-lg px-6 pt-3 pb-8 animate-slide-up"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Handle bar */}
        <div className="w-10 h-1 bg-gray-200 rounded-full mx-auto mb-5" />

        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-5 w-9 h-9 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition z-10"
        >
          <X className="w-5 h-5 text-gray-500" />
        </button>

        {/* Header */}
        <div className="flex flex-col items-center mb-8 mt-2">
          <span className="text-5xl mb-3">🌐</span>
          <h2 className="text-2xl font-bold text-gray-900 tracking-tight mb-1.5">
            {t("chooseYourLanguage")}
          </h2>
          <p className="text-base text-gray-500">
            {t("selectPreferredLanguage")}
          </p>
        </div>

        {/* Language options */}
        <div className="space-y-3 mb-6">
          {languages.map(({ code, name, flag }) => {
            const isSelected = selectedLang === code;
            return (
              <button
                key={code}
                onClick={() => setSelectedLang(code)}
                className={`w-full flex items-center justify-between rounded-2xl p-4 border-2 transition-all active:scale-95 ${
                  isSelected
                    ? "bg-blue-50 border-blue-500"
                    : "bg-gray-50 border-gray-100 hover:border-gray-200"
                }`}
              >
                <div className="flex items-center gap-4">
                  <span className="text-3xl">{flag}</span>
                  <span
                    className={`text-lg font-semibold ${
                      isSelected ? "text-blue-700" : "text-gray-700"
                    }`}
                  >
                    {name}
                  </span>
                </div>
                {isSelected && (
                  <div className="w-7 h-7 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-white text-sm font-bold">✓</span>
                  </div>
                )}
              </button>
            );
          })}
        </div>

        {/* Confirm button */}
        <button
          onClick={handleConfirm}
          disabled={!selectedLang}
          className={`w-full py-4 rounded-2xl text-lg font-bold tracking-wide text-white transition ${
            !selectedLang
              ? "bg-gray-300 cursor-not-allowed"
              : "bg-blue-500 hover:bg-blue-600 shadow-lg"
          }`}
        >
          {t("ok")}
        </button>
      </div>

      <style>{`
        @keyframes slide-up {
          from { transform: translateY(100%); }
          to   { transform: translateY(0); }
        }
        .animate-slide-up { animation: slide-up 0.3s ease-out; }
      `}</style>
    </div>
  );
}
